import React, { useState, useEffect, useCallback, useRef } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { View, StyleSheet, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import MessagesContainer from "./MessagesContainer";
import type { MessageItem } from "./companionMessage.types";
import ChatEmptyState from "./ChatEmptyState";
import {
  type Todo,
  AttachmentType,
  ImageBlockStatus,
  useSwitchBranchMutation,
  useSetMessageFeedbackMutation,
  useGetAiTutorLazyQuery,
  useGetLearningTtsTokenMutation,
} from "@axio/graphql/generated/graphql";
import { useImageGenerationPolling } from "../../hooks/useImageGenerationPolling";
import { useIleCreationPolling } from "../../hooks/useIleCreationPolling";
import { useDeepResearchPolling } from "../../hooks/useDeepResearchPolling";
import type { ToolId } from "../../components/chatNewInterface/interfaceComponents/ToolsMenu/toolOptions";
import useCompanionLlmStream from "../../hooks/useCompanionLlmStream";
import { v4 as uuidv4 } from "uuid";
import { WorkspaceHeader } from "../../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import { useWorkspaceThreads } from "../../contexts/CompanionThreadsContext";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import ChatInputController from "./ChatInputController";
import { Assignment } from "../../components/chatNewInterface/interfaceComponents/modals/AddAssignmentModal";
import { MathVirtualKeyboardProvider } from "../../contexts/MathVirtualKeyboardContext";
import { colors, spacing, semanticColors, radii, typographyStyles } from "../../styles/design-system";
import type { AttachmentInputType, InitialChatMessage } from "./types";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { GENERAL_WORKSPACE_ID } from "../../hooks/useWorkspaceLists";
import { ThreadModals } from "../../components/chat-companion/ThreadList/ThreadModals";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import Tooltip from "../../components/common/tooltip/Tooltip";
import CompanionErrorModal from "../../components/companion/CompanionErrorModal/CompanionErrorModal";
import { useAITutorMessagesReadAloud } from "../chat/AITutorChatContainer/useAITutorMessagesReadAloud";
import { useChatAudioPreferences } from "../../contexts/ChatAudioPreferencesContext";
import { useDeepResearch } from "../../hooks/useDeepResearch";
const NEW_CHAT_KEY = "new-chat";

interface ChatWindowContainerProps {
  chatId: string | null;
  onChatIdChange?: (chatId: string) => void;
  isLoadingChatId?: boolean;
  initialMessage?: InitialChatMessage;
  onInitialMessageConsumed?: () => void;
}

const ChatWindowContainer = ({
  chatId,
  onChatIdChange,
  isLoadingChatId = false,
  initialMessage,
  onInitialMessageConsumed,
}: ChatWindowContainerProps) => {
  const [messagesByChatId, setMessagesByChatId] = useState<Record<string, MessageItem[]>>({});
  const [streamingMessageIds, setStreamingMessageIds] = useState<Record<string, string | null>>({});
  const [streamingChatKey, setStreamingChatKey] = useState<string | null>(null);
  const [resetAutoScroll, setResetAutoScroll] = useState(0);
  const [preselectedAssignment, setPreselectedAssignment] = useState<Assignment | null>(null);
  const [editingMessage, setEditingMessage] = useState<{ id: string; index: number; draft: string } | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [feedbackByMessageId, setFeedbackByMessageId] = useState<Record<string, "up" | "down">>({});
  const { isAudioOn, playbackSpeed } = useChatAudioPreferences();
  const [readAloudMessageIdRemapEvent, setReadAloudMessageIdRemapEvent] = useState<{
    fromMessageId: string;
    toMessageId: string;
    nonce: number;
  } | null>(null);
  const [autoReadAloudMessageIdByChatKey, setAutoReadAloudMessageIdByChatKey] = useState<Record<string, string | null>>(
    {}
  );
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const {
    activeWorkspaceId,
    setActiveWorkspaceId,
    setOpenWorkspaceId,
    refetchPreviewChats,
    applyStreamChatRename,
    getThreadName: getThreadNameFromContext,
    getWorkspaceName,
    courseWorkspaces,
    activeChatThread,
    activeChatThreadLoading,
    activeChatError,
    refetchActiveChat,
    showError,
    onThreadRename,
  } = useWorkspaceThreads();
  const hasStreamedRef = useRef<Set<string>>(new Set());
  const deepResearchBackgroundPendingRef = useRef(false);
  const companionStreamCleanupRef = useRef<(() => void) | null>(null);
  const prevChatIdRef = useRef<string | null>(null);
  const latestChatIdRef = useRef<string | null>(chatId);
  const hasFirstTokenRef = useRef(false);
  const isMountedRef = useRef(true);
  const isAudioOnRef = useRef(isAudioOn);
  const [hasReceivedFirstToken, setHasReceivedFirstToken] = useState(false);
  const [streamStatusMessage, setStreamStatusMessage] = useState<string | null>(null);
  const [deepResearchChatKey, setDeepResearchChatKey] = useState<string | null>(null);
  const [ileUiChatKey, setIleUiChatKey] = useState<string | null>(null);
  const [fetchAiTutorById] = useGetAiTutorLazyQuery();
  const startImageGenerationPollingRef = useRef<
    ((jobId: string, streamChatKey: string, aiMessageId: string) => void) | null
  >(null);

  const { startStream, cancelStream, isStreaming } = useCompanionLlmStream();

  const [switchBranch] = useSwitchBranchMutation();
  const [setMessageFeedbackMutation] = useSetMessageFeedbackMutation();
  const [getLearningTtsToken] = useGetLearningTtsTokenMutation();
  const initialMessageKeyRef = useRef<string | null>(null);
  const initialWorkspaceIdRef = useRef<string | null>(null);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const {
    deepResearchState,
    startDeepResearch,
    activateDeepResearchProgress,
    resetDeepResearch,
    handleDeepResearchStateEvent,
    handleDeepResearchStreamComplete,
  } = useDeepResearch();

  useEffect(() => {
    if (initialMessage?.workspaceId !== undefined) {
      if (initialMessage.workspaceId !== GENERAL_WORKSPACE_ID) {
        initialWorkspaceIdRef.current = initialMessage.workspaceId;
      } else {
        initialWorkspaceIdRef.current = null;
      }
      setActiveWorkspaceId(initialMessage.workspaceId);
      if (initialMessage.workspaceId !== GENERAL_WORKSPACE_ID) {
        setOpenWorkspaceId(initialMessage.workspaceId);
      } else {
        setOpenWorkspaceId(null);
      }
    }
  }, [initialMessage?.workspaceId, setActiveWorkspaceId, setOpenWorkspaceId]);

  const chatWorkspaces = activeChatThread?.workspacesHasChat ?? [];

  const resolvedWorkspaceId =
    activeWorkspaceId && chatWorkspaces.some((w) => w.id === activeWorkspaceId)
      ? activeWorkspaceId
      : (chatWorkspaces[0]?.id ?? activeWorkspaceId ?? GENERAL_WORKSPACE_ID);

  const getThreadName = useCallback(
    (threadId: string | null): string => {
      if (!threadId) return "";

      const contextName = getThreadNameFromContext(threadId);
      if (contextName) {
        return contextName;
      }

      return activeChatThread?.id === threadId ? (activeChatThread?.name ?? "") : "";
    },
    [activeChatThread, getThreadNameFromContext]
  );

  const currentWorkspaceType: "general" | "course" = (() => {
    if (resolvedWorkspaceId === GENERAL_WORKSPACE_ID) {
      return "general";
    }

    // First try to get type from chat data (when chatId exists)
    const workspaceFromChat = chatWorkspaces.find((w) => w.id === resolvedWorkspaceId);
    if (workspaceFromChat?.type) {
      return workspaceFromChat.type === "course" ? "course" : "general";
    }

    // Fallback to workspace lists from context (for new chats during streaming)
    if (courseWorkspaces.some((w) => w.id === resolvedWorkspaceId)) {
      return "course";
    }

    return "general";
  })();

  const workspaceName = getWorkspaceName(resolvedWorkspaceId) ?? "General Chat";
  const activeThreadTitle = getThreadName(chatId);
  const normalizedThreadTitle = (activeThreadTitle ?? "").trim();
  const canRenameThread = Boolean(chatId && normalizedThreadTitle.length > 0 && onThreadRename);

  const handleOpenRenameModal = useCallback(() => {
    setRenameValue(normalizedThreadTitle);
    setRenameModalOpen(true);
  }, [normalizedThreadTitle]);

  const handleRenameSubmit = useCallback(() => {
    if (chatId && renameValue.trim() && onThreadRename) {
      void onThreadRename(chatId, renameValue.trim());
      setRenameModalOpen(false);
      setRenameValue("");
    }
  }, [chatId, renameValue, onThreadRename]);

  const getChatKey = useCallback((id: string | null) => id ?? NEW_CHAT_KEY, []);
  const currentChatKey = getChatKey(chatId);
  const messages = messagesByChatId[currentChatKey] ?? [];
  const currentStreamingMessageId = streamingMessageIds[currentChatKey] ?? null;
  const isStreamingForCurrentChat = isStreaming && streamingChatKey === currentChatKey;
  const scopedDeepResearchState = deepResearchChatKey === currentChatKey ? deepResearchState : null;

  useEffect(() => {
    isAudioOnRef.current = isAudioOn;
  }, [isAudioOn]);

  const getTtsToken = useCallback(async () => {
    try {
      const result = await getLearningTtsToken();
      return result.data?.getLearningTtsToken?.token ?? null;
    } catch {
      return null;
    }
  }, [getLearningTtsToken]);

  const { getAssistantReadAloudControl } = useAITutorMessagesReadAloud({
    resourceId: currentChatKey,
    messages,
    messageReadAloudTts: {
      resetPlayback: () => undefined,
      suspendPlayback: () => undefined,
      resumePlayback: () => undefined,
      getTtsToken,
    },
    autoReadAloudEnabled: isAudioOn,
    autoReadAloudMessageId: autoReadAloudMessageIdByChatKey[currentChatKey] ?? null,
    readAloudMessageIdRemapEvent,
    playbackSpeed,
    isScreenFocused: true,
    onUserFocusCleanup: () => {},
  });

  useEffect(() => {
    latestChatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    if (deepResearchState == null && deepResearchChatKey != null) {
      setDeepResearchChatKey(null);
    }
  }, [deepResearchChatKey, deepResearchState]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const prevChatId = prevChatIdRef.current;
    prevChatIdRef.current = chatId;
    const isInitialStreamCompletionTransition = !prevChatId && Boolean(chatId);
    if (!isInitialStreamCompletionTransition) {
      setReadAloudMessageIdRemapEvent(null);
      setAutoReadAloudMessageIdByChatKey({});
    }

    //  New chat button: chatA → null
    if (prevChatId && !chatId) {
      setPreselectedAssignment(null);
      setResetAutoScroll((prev) => prev + 1);
      setMessagesByChatId((prev) => ({ ...prev, [NEW_CHAT_KEY]: [] }));
      setStreamingMessageIds((prev) => ({ ...prev, [NEW_CHAT_KEY]: null }));
      initialWorkspaceIdRef.current = null;
      return;
    }

    // Switching chats: chatA → chatB
    if (prevChatId && chatId && prevChatId !== chatId) {
      setResetAutoScroll((prev) => prev + 1);
      return;
    }

    //  First stream completion: null → chatId
    // DO NOTHING
  }, [chatId]);

  useEffect(() => {
    const conversation = activeChatThread?.conversation;
    const dataChatId = activeChatThread?.id;

    if (!chatId) return;
    if (!conversation) return;
    if (dataChatId !== chatId) return;

    // HARD STOP: stream owns messages
    if (hasStreamedRef.current.has(chatId)) {
      return;
    }

    const conversationMessages: MessageItem[] = conversation
      .map((msg) => {
        const metadata = (msg as { metadata?: MessageItem["metadata"] }).metadata;
        const createdILEFromMessage = (msg as { createdILE?: MessageItem["createdILE"] }).createdILE;
        const deepResearchDurationSeconds = (msg as { deepResearchDurationSeconds?: number | null })
          .deepResearchDurationSeconds;
        const deepResearchSources = (
          msg as { deepResearchSources?: Array<{ id: string; url?: string | null; title?: string | null }> }
        ).deepResearchSources;
        const imageBlocksFromMsg = (
          msg as { imageBlocks?: Array<{ id: string; status?: string; url?: string | null; jobId?: string | null }> }
        ).imageBlocks;
        const imageBlockFromMsg = imageBlocksFromMsg?.[0];

        const deepResearch =
          (typeof deepResearchDurationSeconds === "number" && !Number.isNaN(deepResearchDurationSeconds)) ||
          (deepResearchSources != null && deepResearchSources.length > 0)
            ? {
                durationSeconds: Math.max(0, deepResearchDurationSeconds ?? 0),
                sources: (deepResearchSources ?? []).map((s) => ({
                  id: s.id,
                  url: s.url ?? "",
                  title: s.title ?? undefined,
                  type: "web" as const,
                })),
              }
            : undefined;

        return {
          id: msg.id,
          index: msg.index,
          sender: msg.sender || "ai",
          content: msg.content || "",
          created: msg.created || undefined,
          todos: msg.todos as Todo[],
          assignments: msg.assignments as Assignment[],
          files: msg.files,
          selectedBranchId: msg.selectedBranchId ?? undefined,
          branches: msg.branches ?? [],
          metadata,
          createdILE: createdILEFromMessage ?? metadata?.createdILE ?? null,
          imageBlocks: imageBlockFromMsg
            ? [
                {
                  id: imageBlockFromMsg.id,
                  status: imageBlockFromMsg.status as ImageBlockStatus,
                  url: imageBlockFromMsg.url ?? null,
                  jobId: imageBlockFromMsg.jobId ?? null,
                },
              ]
            : undefined,
          deepResearch,
        };
      })
      .reverse()
      // Use backend index when present (unique along visible path); fallback to display position for edit/revert UX.
      .map((msg, i) => ({ ...msg, index: msg.index ?? i }));

    setMessagesByChatId((prev) => ({
      ...prev,
      [chatId]: conversationMessages,
    }));

    // Resume polling for in-progress image generation (single image block per message).
    for (const msg of conversationMessages) {
      const imageBlock = msg.imageBlocks?.[0];
      if (imageBlock?.status === ImageBlockStatus.Generating && imageBlock.jobId) {
        startImageGenerationPollingRef.current?.(imageBlock.jobId, chatId, msg.id);
      }
    }

    const initialFeedback: Record<string, "up" | "down"> = {};
    for (const msg of conversation) {
      const fb = (msg as any).feedback;
      if (fb === "up" || fb === "down") {
        initialFeedback[msg.id] = fb;
      }
    }
    setFeedbackByMessageId(initialFeedback);

    if (deepResearchState?.phase === "result" && conversationMessages.some((message) => message.deepResearch)) {
      resetDeepResearch();
    }
  }, [chatId, activeChatThread, deepResearchState?.phase, resetDeepResearch]);

  const handleChatRenamed = useCallback(
    async (payload: { chatId: string; name: string }) => {
      try {
        applyStreamChatRename(payload.chatId, payload.name);
        await refetchPreviewChats();
        if (payload.chatId === chatId || payload.chatId === latestChatIdRef.current) {
          await refetchActiveChat();
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to refresh the thread list.";
        showError({
          title: "Failed to refresh thread list",
          message,
          onRetry: async () => {
            applyStreamChatRename(payload.chatId, payload.name);
            await refetchPreviewChats();
            if (payload.chatId === chatId || payload.chatId === latestChatIdRef.current) {
              await refetchActiveChat();
            }
          },
          retryButtonText: "Retry",
        });
      }
    },
    [applyStreamChatRename, refetchPreviewChats, refetchActiveChat, chatId, showError]
  );

  const handleFeedback = useCallback(async (messageId: string, value: "up" | "down" | null) => {
    setFeedbackByMessageId((prev) => {
      if (value === null) {
        const next = { ...prev };
        delete next[messageId];
        return next;
      }
      return { ...prev, [messageId]: value };
    });
    datadogRum.addAction("message_feedback", { messageId, chatType: "companion", value });
  }, []);

  const handleStartEdit = useCallback((messageId: string, messageIndex: number, text: string) => {
    setEditingMessage({ id: messageId, index: messageIndex, draft: text });
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingMessage(null);
  }, []);

  const handleChangeEditingDraft = useCallback((value: string) => {
    setEditingMessage((prev) => (prev ? { ...prev, draft: value } : prev));
  }, []);

  const handleSwitchBranch = useCallback(
    async (anchorMessageId: string, targetBranchId: string | null) => {
      if (!chatId || isStreamingForCurrentChat) return;

      try {
        const { data } = await switchBranch({
          variables: {
            chatId,
            anchorMessageId,
            targetBranchId,
          },
        });

        if (data?.switchBranch?.success) {
          // Allow refetch to overwrite local messages for this chat (we're not streaming now).
          hasStreamedRef.current.delete(chatId);
          // Trigger a refetch of the active chat to get the new timeline
          // setResetAutoScroll((prev) => prev + 1);
          await refetchActiveChat();
        } else {
          showError({
            title: "Failed to switch branch",
            message: data?.switchBranch?.message ?? "An error occurred while switching versions.",
          });
        }
      } catch (error) {
        console.error("[ChatWindowContainer] handleSwitchBranch failed", error);
        showError({
          title: "Failed to switch branch",
          message: error instanceof Error ? error.message : "An unexpected error occurred.",
        });
      }
    },
    [chatId, isStreamingForCurrentChat, switchBranch, refetchActiveChat, showError]
  );

  const {
    startImageGenerationPolling,
    stopImageGenerationPollingForMessage,
    remapImagePollMessageId,
    remapImagePollChatKey,
  } = useImageGenerationPolling(setMessagesByChatId);
  startImageGenerationPollingRef.current = startImageGenerationPolling;

  const hydrateCreatedILE = useCallback(
    async (createdILE: MessageItem["createdILE"] | null | undefined): Promise<MessageItem["createdILE"]> => {
      if (!createdILE?.id || (createdILE.objectives && createdILE.objectives.length > 0)) {
        return createdILE ?? null;
      }
      try {
        const aiTutorResult = await fetchAiTutorById({
          variables: { id: createdILE.id },
          fetchPolicy: "network-only",
        });
        const hydratedAiTutor = aiTutorResult.data?.getAITutor?.data?.[0];
        if (!hydratedAiTutor) {
          return createdILE;
        }
        return {
          id: hydratedAiTutor.id,
          title: hydratedAiTutor.title,
          description: hydratedAiTutor.description ?? null,
          objectives: (hydratedAiTutor.objectives ?? []).map((objective) => ({
            id: objective.id,
            objective: objective.objective ?? null,
            sequence: objective.sequence ?? null,
            evalCriteria: objective.evalCriteria ?? [],
          })),
        };
      } catch (ileError) {
        console.warn("[ChatWindowContainer] Failed to hydrate ILE objectives", ileError);
        return createdILE;
      }
    },
    [fetchAiTutorById]
  );

  const {
    startIleCreationPolling,
    stopIleCreationPolling,
    remapIlePollMessageId,
    remapIlePollChatKey,
    failIleCreation,
    setIleCreationErrorMessage,
    isIleCreationInProgress,
    ileCreationErrorMessage: streamIleCreationError,
  } = useIleCreationPolling({ setMessagesByChatId, hydrateCreatedILE, refetchActiveChat });

  const showIleCreationLoader = ileUiChatKey === currentChatKey && isIleCreationInProgress;
  const scopedIleCreationErrorMessage = ileUiChatKey === currentChatKey ? streamIleCreationError : null;

  useEffect(() => {
    if (!isIleCreationInProgress && !streamIleCreationError && ileUiChatKey != null) {
      setIleUiChatKey(null);
    }
  }, [ileUiChatKey, isIleCreationInProgress, streamIleCreationError]);

  const {
    startDeepResearchPolling,
    remapDeepResearchPollMessageId,
    remapDeepResearchPollChatKey,
    cancelActiveDeepResearch,
    activeDeepResearchPollTarget,
    isDeepResearchPolling,
  } = useDeepResearchPolling({
    refetchActiveChat,
    refetchPreviewChats,
    onBeforeDeepResearchRefetch: () => {
      const id = chatId ?? latestChatIdRef.current;
      if (id) {
        hasStreamedRef.current.delete(id);
      }
    },
    onHeartbeat: (elapsedSeconds) => {
      handleDeepResearchStateEvent({ state: "heartbeat", payload: { elapsedSeconds } });
    },
    onTerminal: ({ status, streamChatKey, messageId }) => {
      deepResearchBackgroundPendingRef.current = false;
      if (status === "completed") {
        handleDeepResearchStreamComplete();
        if (isAudioOnRef.current && streamChatKey && messageId) {
          // Defer until after activeChatThread → messagesByChatId sync (refetch .then can run before that useEffect).
          setTimeout(() => {
            setAutoReadAloudMessageIdByChatKey((prev) => ({
              ...prev,
              [streamChatKey]: messageId,
            }));
          }, 0);
        }
        return;
      }
      if (
        status === "cancelled" ||
        status === "failed" ||
        status === "forbidden" ||
        status === "not_found" ||
        status === "error"
      ) {
        resetDeepResearch();
      }
    },
  });

  useEffect(() => {
    if (!activeDeepResearchPollTarget) return;
    if (deepResearchChatKey === activeDeepResearchPollTarget.streamChatKey && deepResearchState?.phase === "progress") {
      return;
    }

    startDeepResearch();
    setDeepResearchChatKey(activeDeepResearchPollTarget.streamChatKey);
    activateDeepResearchProgress(activeDeepResearchPollTarget.startedAtMs);
  }, [
    activeDeepResearchPollTarget,
    activateDeepResearchProgress,
    deepResearchChatKey,
    deepResearchState?.phase,
    startDeepResearch,
  ]);

  const handleCancelDeepResearch = useCallback(() => {
    const mode = deepResearchState?.progress?.progressMode;
    if (mode === "preparing") {
      cancelStream();
      companionStreamCleanupRef.current?.();
      return;
    }
    if (isDeepResearchPolling) {
      void cancelActiveDeepResearch();
      return;
    }
    cancelStream();
    companionStreamCleanupRef.current?.();
  }, [cancelStream, deepResearchState?.progress?.progressMode, isDeepResearchPolling, cancelActiveDeepResearch]);

  const handleSubmit = useCallback(
    async (
      message: string,
      attachment: AttachmentInputType | null,
      options?: {
        workspaceIdOverride?: string | null;
        activeTool?: ToolId | null;
        /**
         * When set, treat this as an edit-driven replay:
         * - do NOT append a new user message locally; stream creates it via editChat
         * - only append an AI placeholder and start a new stream
         * - anchorMessageId: the message being edited (required for stream to call editChat).
         * - previousContent and messageIndex: used to revert optimistic update and re-open edit mode on stream error.
         */
        afterEdit?: {
          truncatedMessages?: MessageItem[];
          anchorMessageId?: string;
          previousContent?: string;
          messageIndex?: number;
        };
      }
    ) => {
      if (!message.trim() || isStreaming) {
        return;
      }

      const trimmedInput = message.trim();
      const isAfterEdit = options?.afterEdit != null;
      const anchorMessageId = options?.afterEdit?.anchorMessageId;
      if (isAfterEdit && !anchorMessageId) {
        showError({
          title: "Cannot replay after edit",
          message: "The message being edited could not be determined. Please try again.",
        });
        return;
      }
      const baseMessages = options?.afterEdit?.truncatedMessages;

      const attachedTodos =
        attachment?.type === "todo" ? [{ id: attachment.id, name: attachment.name, date: attachment.date }] : [];
      const attachedAssigment =
        attachment?.type === "assignment"
          ? [{ id: attachment.id, name: attachment.name, due_date: attachment.date }]
          : [];
      const attachedFiles =
        attachment?.type === AttachmentType.Document && attachment.fileId && attachment.name
          ? [{ id: attachment.fileId, fileName: attachment.name }]
          : [];

      const userMessage: MessageItem = {
        id: uuidv4(),
        sender: "human",
        content: trimmedInput,
        todos: attachedTodos as Todo[],
        assignments: attachedAssigment as Assignment[],
        files: attachedFiles,
      };

      const aiTempId = uuidv4();
      const streamChatKey = getChatKey(chatId);

      setMessagesByChatId((prev) => {
        const base = isAfterEdit ? (baseMessages ?? prev[streamChatKey] ?? []) : (prev[streamChatKey] ?? []);
        // Edit replay: hide all messages after the edited one while the new response streams
        const existing =
          isAfterEdit && anchorMessageId
            ? (() => {
                const anchorIdx = base.findIndex((m) => m.id === anchorMessageId);
                return anchorIdx === -1 ? base : base.slice(0, anchorIdx + 1);
              })()
            : base;
        const toAppend = isAfterEdit
          ? [{ id: aiTempId, sender: "ai" as const, content: "" }]
          : [userMessage, { id: aiTempId, sender: "ai" as const, content: "" }];
        return {
          ...prev,
          [streamChatKey]: [...existing, ...toAppend],
        };
      });

      setStreamingMessageIds((prev) => ({ ...prev, [streamChatKey]: aiTempId }));
      setStreamingChatKey(streamChatKey);
      setResetAutoScroll((prev) => prev + 1);
      setPreselectedAssignment(null);
      setHasReceivedFirstToken(false);
      hasFirstTokenRef.current = false;

      const cleanupStream = () => {
        deepResearchBackgroundPendingRef.current = false;
        companionStreamCleanupRef.current = null;
        resetDeepResearch();
        setMessagesByChatId((prev) => ({
          ...prev,
          [streamChatKey]: (prev[streamChatKey] ?? []).filter((m) => m.id !== aiTempId),
        }));
        setStreamingMessageIds((prev) => ({ ...prev, [streamChatKey]: null }));
        setStreamingChatKey(null);
        setStreamStatusMessage(null);
        setIleCreationErrorMessage(null);
        stopIleCreationPolling();
        stopImageGenerationPollingForMessage(aiTempId);
      };

      companionStreamCleanupRef.current = cleanupStream;

      const revertEditOnError = () => {
        const afterEdit = options?.afterEdit;
        if (!afterEdit?.anchorMessageId || afterEdit.previousContent === undefined) return;
        setMessagesByChatId((prev) => {
          const key = getChatKey(chatId);
          const list = prev[key] ?? [];
          return {
            ...prev,
            [key]: list.map((m) =>
              m.id === afterEdit.anchorMessageId ? { ...m, content: afterEdit.previousContent ?? m.content } : m
            ),
          };
        });
      };

      try {
        const resolveWorkspaceId = () => {
          if (options && Object.prototype.hasOwnProperty.call(options, "workspaceIdOverride")) {
            const override = options.workspaceIdOverride;
            if (!override || override === GENERAL_WORKSPACE_ID) {
              return undefined;
            }
            return override;
          }
          if (activeWorkspaceId && activeWorkspaceId !== GENERAL_WORKSPACE_ID) {
            return activeWorkspaceId;
          }
          return undefined;
        };
        const workspaceId = resolveWorkspaceId();

        if (chatId) {
          hasStreamedRef.current.add(chatId);
        }

        const newestToOldest = messages.slice().reverse();
        const messageWithBranch = newestToOldest.find((m) => m.selectedBranchId != null);
        const currentBranchId =
          !isAfterEdit && chatId && messages.length > 0 ? (messageWithBranch?.selectedBranchId ?? null) : undefined;

        setAutoReadAloudMessageIdByChatKey((prev) => ({ ...prev, [streamChatKey]: null }));
        const imageGenerationEnabled = options?.activeTool === "createImage";
        const deepResearchEnabled = options?.activeTool === "deepResearch";

        if (deepResearchEnabled) {
          startDeepResearch();
          setDeepResearchChatKey(streamChatKey);
        }

        const streamTools =
          deepResearchEnabled || imageGenerationEnabled
            ? {
                ...(deepResearchEnabled ? { deepResearch: true as const } : {}),
                ...(imageGenerationEnabled ? { imageGenerationEnabled: true as const } : {}),
              }
            : undefined;

        await startStream(trimmedInput, {
          chatId: chatId || undefined,
          workspaceId,
          tools: streamTools,
          userMessageId: isAfterEdit ? undefined : userMessage.id,
          isEditReplay: isAfterEdit,
          anchorMessageId: isAfterEdit ? anchorMessageId : undefined,
          branchId: currentBranchId,
          attachment:
            attachment != null
              ? {
                  fileId: attachment.fileId,
                  id: attachment.id,
                  type: attachment.type,
                }
              : undefined,
          onChunk: ({ text }) => {
            if (deepResearchEnabled) {
              if (text && !hasReceivedFirstToken) {
                hasFirstTokenRef.current = true;
                setHasReceivedFirstToken(true);
              }
              return;
            }

            if (text && !hasReceivedFirstToken) {
              hasFirstTokenRef.current = true;
              setHasReceivedFirstToken(true);
            }
            setMessagesByChatId((prev) => ({
              ...prev,
              [streamChatKey]: (prev[streamChatKey] ?? []).map((m) =>
                m.id === aiTempId ? { ...m, content: m.content + text } : m
              ),
            }));
          },
          onStatus: ({ data }) => {
            if (deepResearchEnabled) {
              return;
            }
            setStreamStatusMessage(data?.trim() || null);
          },
          onToolStart: ({ tool, jobId }) => {
            if (tool === "ile_creation" && jobId) {
              setIleUiChatKey(streamChatKey);
              setStreamStatusMessage("Creating your learning activity...");
              startIleCreationPolling(jobId, streamChatKey, aiTempId);
            }
            if (tool === "deep_research" && jobId) {
              deepResearchBackgroundPendingRef.current = true;
              activateDeepResearchProgress();
              startDeepResearchPolling(jobId, streamChatKey, aiTempId);
            }
            if (tool === "image_generation" && jobId) {
              startImageGenerationPolling(jobId, streamChatKey, aiTempId);
              setMessagesByChatId((prev) => ({
                ...prev,
                [streamChatKey]: (prev[streamChatKey] ?? []).map((m) =>
                  m.id === aiTempId
                    ? {
                        ...m,
                        imageBlocks: [
                          {
                            id: `temp-image-block-${jobId}`,
                            status: ImageBlockStatus.Generating,
                            jobId,
                          },
                        ],
                      }
                    : m
                ),
              }));
            }
          },
          onToolResult: ({ tool, result }) => {
            if (tool === "ile_creation") {
              const resultWithError = result as { error?: boolean; code?: string; message?: string; id?: string };
              if (resultWithError?.error && resultWithError?.message) {
                failIleCreation(resultWithError.message);
                return;
              }

              const resultWithCreatedIle = result as {
                createdILE?: MessageItem["createdILE"];
                status?: string;
                success?: boolean;
              };
              if (
                resultWithCreatedIle?.status === "completed" &&
                resultWithCreatedIle?.success &&
                resultWithCreatedIle?.createdILE
              ) {
                setMessagesByChatId((prev) => ({
                  ...prev,
                  [streamChatKey]: (prev[streamChatKey] ?? []).map((m) =>
                    m.id === aiTempId ? { ...m, createdILE: resultWithCreatedIle.createdILE ?? null } : m
                  ),
                }));
                setIleCreationErrorMessage(null);
                stopIleCreationPolling();
              }
            }
          },
          onDeepSearchState: ({ state, payload }) => {
            handleDeepResearchStateEvent({ state, payload: payload ?? undefined });
          },
          onComplete: async ({ event }) => {
            companionStreamCleanupRef.current = null;
            setStreamStatusMessage(null);
            // Do not clear streamIleCreationError here—keep the error visible until the next ILE flow or message
            const newChatId = event?.chatId && streamChatKey === NEW_CHAT_KEY ? event.chatId : undefined;
            const messageId = event?.messageId;
            if (messageId) {
              if (messageId !== aiTempId) {
                setReadAloudMessageIdRemapEvent({
                  fromMessageId: aiTempId,
                  toMessageId: messageId,
                  nonce: Date.now(),
                });
              }
              const metadata = event?.metadata;
              const resolvedCreatedILE = await hydrateCreatedILE(metadata?.createdILE);

              const createdILEForMessage = resolvedCreatedILE ?? null;
              const resolvedMetadata =
                resolvedCreatedILE && metadata
                  ? { ...metadata, createdILE: resolvedCreatedILE }
                  : metadata
                    ? { ...metadata, createdILE: undefined }
                    : undefined;

              setMessagesByChatId((prev) => ({
                ...prev,
                [streamChatKey]: (prev[streamChatKey] ?? []).map((m) =>
                  m.id === aiTempId
                    ? {
                        ...m,
                        id: messageId,
                        ...(resolvedMetadata ? { metadata: resolvedMetadata } : {}),
                        createdILE: createdILEForMessage ?? m.createdILE ?? null,
                      }
                    : m
                ),
              }));
              if (event.messageId) {
                remapImagePollMessageId(aiTempId, event.messageId);
                remapIlePollMessageId(aiTempId, event.messageId);
                remapDeepResearchPollMessageId(aiTempId, event.messageId);
              }
            }
            if (event?.chatId) {
              hasStreamedRef.current.add(event.chatId);
            }
            if (newChatId) {
              setMessagesByChatId((prev) => {
                const next = { ...prev };
                const newChatMessages = next[NEW_CHAT_KEY] ?? [];
                delete next[NEW_CHAT_KEY];
                next[newChatId] = newChatMessages;
                return next;
              });
              setDeepResearchChatKey((prev) => (prev === NEW_CHAT_KEY ? newChatId : prev));
              setIleUiChatKey((prev) => (prev === NEW_CHAT_KEY ? newChatId : prev));
              remapImagePollChatKey(NEW_CHAT_KEY, newChatId);
              remapIlePollChatKey(NEW_CHAT_KEY, newChatId);
              remapDeepResearchPollChatKey(NEW_CHAT_KEY, newChatId);
            }
            setStreamingMessageIds((prev) => {
              const next = { ...prev };
              if (newChatId) {
                const newChatStreamingId = next[NEW_CHAT_KEY] ?? null;
                delete next[NEW_CHAT_KEY];
                next[newChatId] = newChatStreamingId;
                return next;
              }
              next[streamChatKey] = null;
              return next;
            });
            setStreamingChatKey(null);
            if (event?.chatId && options?.afterEdit != null) {
              hasStreamedRef.current.delete(event.chatId);
              void refetchActiveChat();
            }
            if (newChatId) {
              // Use the workspaceId captured at submit time
              if (workspaceId && workspaceId !== GENERAL_WORKSPACE_ID) {
                setActiveWorkspaceId(workspaceId);
              } else {
                setActiveWorkspaceId(GENERAL_WORKSPACE_ID);
              }
              if (!latestChatIdRef.current && onChatIdChange) {
                onChatIdChange(newChatId);
              }
              // Refetch the sidebar preview query to update thread counts
              try {
                await refetchPreviewChats();
              } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to refresh the thread list.";
                showError({
                  title: "Failed to refresh thread list",
                  message,
                  onRetry: async () => {
                    await refetchPreviewChats();
                  },
                  retryButtonText: "Retry",
                });
              }
            }

            const resolvedAutoPlayMessageId = messageId ?? aiTempId;
            const resolvedAutoPlayChatKey = newChatId ?? streamChatKey;
            if (isAudioOnRef.current && resolvedAutoPlayMessageId && !deepResearchBackgroundPendingRef.current) {
              setAutoReadAloudMessageIdByChatKey((prev) => ({
                ...prev,
                [resolvedAutoPlayChatKey]: resolvedAutoPlayMessageId,
              }));
            }

            if (deepResearchEnabled && !deepResearchBackgroundPendingRef.current) {
              resetDeepResearch();
            }
          },
          onError: ({ error }) => {
            console.error("[ChatWindowContainer] Stream error", { message: error.message });
            cleanupStream();
            if (options?.afterEdit != null) {
              revertEditOnError();
            }
            showError({
              title: "Failed to send message",
              message: error.message || "An error occurred while sending your message. Please try again.",
              onRetry: async () => {
                await handleSubmit(trimmedInput, attachment ?? null, options);
              },
              retryButtonText: "Retry",
            });
          },
          onChatRenamed: handleChatRenamed,
        });
      } catch (error) {
        // Revert optimistic edit first on any failure (e.g. "Unable to start ... GraphQL SSE stream")
        if (options?.afterEdit != null) {
          revertEditOnError();
        }
        console.error("[ChatWindowContainer] Failed to start stream:", error);
        cleanupStream();
        const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
        showError({
          title: "Failed to send message",
          message: errorMessage || "Unable to start the conversation. Please try again.",
          onRetry: async () => {
            await handleSubmit(trimmedInput, attachment ?? null, options);
          },
          retryButtonText: "Retry",
        });
      }
    },
    [
      isStreaming,
      chatId,
      messages,
      activeWorkspaceId,
      startStream,
      startDeepResearch,
      activateDeepResearchProgress,
      resetDeepResearch,
      startIleCreationPolling,
      stopIleCreationPolling,
      startImageGenerationPolling,
      stopImageGenerationPollingForMessage,
      getChatKey,
      hasReceivedFirstToken,
      onChatIdChange,
      handleChatRenamed,
      refetchPreviewChats,
      showError,
      hydrateCreatedILE,
    ]
  );

  const handleSaveInlineEdit = useCallback(async () => {
    if (!chatId || !editingMessage || isStreamingForCurrentChat) return;

    const trimmed = editingMessage.draft.trim();
    if (!trimmed) {
      showError({
        title: "Cannot save edit",
        message: "Message text cannot be empty.",
      });
      return;
    }

    const uiIndex = messages.findIndex((m) => m.id === editingMessage.id);
    if (uiIndex < 0) {
      showError({
        title: "Cannot save edit",
        message: "We couldn't find that message in the current thread.",
      });
      return;
    }

    const anchorMessageId = editingMessage.id;

    try {
      hasStreamedRef.current.delete(chatId);
      setResetAutoScroll((prev) => prev + 1);
      setEditingMessage(null);

      // Optimistically update the message in the list to show the new text.
      setMessagesByChatId((prev) => {
        const key = getChatKey(chatId);
        const list = prev[key] ?? [];
        return {
          ...prev,
          [key]: list.map((m) => (m.id === anchorMessageId ? { ...m, content: trimmed } : m)),
        };
      });

      const previousContent = messages[uiIndex]?.content ?? "";
      // Start stream with anchorMessageId and isEditReplay; stream will call editChat then add AI reply.
      // previousContent and messageIndex allow reverting optimistic update and re-opening edit mode on stream error.
      await handleSubmit(trimmed, null, {
        afterEdit: { anchorMessageId, previousContent, messageIndex: uiIndex },
      });
    } catch (error) {
      console.error("[ChatWindowContainer] handleSaveInlineEdit failed", error);
      const message = error instanceof Error ? error.message : "Something went wrong while saving your edit.";
      showError({ title: "Failed to save edit", message });
    }
  }, [chatId, editingMessage, handleSubmit, isStreamingForCurrentChat, messages, showError, getChatKey]);

  const handleOpenLearningActivity = useCallback(
    (ileId: string) => {
      navigation.navigate("Interactive Learning Environment", { activityId: ileId });
    },
    [navigation]
  );

  const handleAssignmentPress = useCallback(
    (assignment: {
      id: string;
      title?: string | null;
      due_date?: string | null;
      courseName?: string;
      courseId?: string;
    }) => {
      const assignmentData: Assignment = {
        id: assignment.id,
        title: assignment.title,
        due_date: assignment.due_date,
        courseName: assignment.courseName,
        courseId: assignment.courseId,
      };
      setPreselectedAssignment(assignmentData);
    },
    []
  );

  const isEmptyState = messages.length === 0 && !isStreamingForCurrentChat && !currentStreamingMessageId;
  const isChatLoading = Boolean(chatId) && (activeChatThreadLoading || isLoadingChatId);
  const shouldShowEmptyState = isEmptyState && !isChatLoading && scopedDeepResearchState?.phase !== "progress";

  useEffect(() => {
    return () => {
      cancelStream();
    };
  }, [cancelStream]);

  useEffect(() => {
    if (!initialMessage?.text?.trim()) {
      initialMessageKeyRef.current = null;
      return;
    }
    if (isStreaming) {
      return;
    }

    const attachmentKey = initialMessage.attachment
      ? `${initialMessage.attachment.type || ""}:${initialMessage.attachment.id || ""}:${
          initialMessage.attachment.fileId || ""
        }:${initialMessage.attachment.name || ""}:${initialMessage.attachment.date || ""}`
      : "no-attachment";
    const activeToolKey = initialMessage.activeTool ?? "no-tool";
    const messageKey = `${initialMessage.text.trim()}::${attachmentKey}::${activeToolKey}`;

    if (initialMessageKeyRef.current === messageKey) {
      return;
    }

    initialMessageKeyRef.current = messageKey;
    const workspaceOverride = initialMessage.workspaceId ?? null;
    // Store the initial workspace ID to prevent it from being overridden
    if (workspaceOverride && workspaceOverride !== GENERAL_WORKSPACE_ID) {
      initialWorkspaceIdRef.current = workspaceOverride;
    } else {
      // Explicitly clear if it's general or null
      initialWorkspaceIdRef.current = null;
    }
    setActiveWorkspaceId(workspaceOverride ?? GENERAL_WORKSPACE_ID);
    void handleSubmit(initialMessage.text, initialMessage.attachment ?? null, {
      workspaceIdOverride: workspaceOverride,
      activeTool: initialMessage.activeTool ?? null,
    });
    onInitialMessageConsumed?.();
  }, [initialMessage, isStreaming, handleSubmit, onInitialMessageConsumed, setActiveWorkspaceId]);

  const showActiveChatError = Boolean(chatId && activeChatError);
  if (showActiveChatError) {
    return (
      <View style={styles.container}>
        <WorkspaceHeader title={workspaceName} headingLevel={2} />
        <View style={styles.errorContainer}>
          <CustomText
            text={activeChatError ?? "We were not able to load the chat thread."}
            size="m"
            useTranslationText={false}
            style={styles.errorMessage}
          />
          <CustomButton
            text="Retry"
            onPress={() => void refetchActiveChat()}
            useTranslationText={false}
            style={styles.retryButton}
          />
        </View>
      </View>
    );
  }

  return (
    <MathVirtualKeyboardProvider>
      <View style={styles.container}>
        <WorkspaceHeader
          title={workspaceName}
          headingLevel={2}
          subtitle={activeThreadTitle}
          middleAction={
            canRenameThread ? (
              <Tooltip content="Click to rename thread" placement="bottom" offset={8} showArrow={false}>
                <Pressable
                  style={(state) => [
                    styles.threadTitleButton,
                    (state as { pressed: boolean; hovered?: boolean }).hovered && styles.threadTitleButtonHovered,
                  ]}
                  onPress={handleOpenRenameModal}
                  accessibilityRole="button"
                  accessibilityLabel="Rename thread">
                  {(state) => {
                    const hovered = (state as { pressed: boolean; hovered?: boolean }).hovered;
                    return (
                      <>
                        <CustomText
                          text={normalizedThreadTitle}
                          size="s"
                          weight="medium"
                          useTranslationText={false}
                          numberOfLines={1}
                          style={styles.threadTitleButtonText}
                        />
                        <View style={[styles.threadTitleIconWrap, !hovered && styles.threadTitleIconHidden]}>
                          <PencilSimpleIcon size={14} color={colors.grey[500]} />
                        </View>
                      </>
                    );
                  }}
                </Pressable>
              </Tooltip>
            ) : null
          }
        />
        <View style={styles.content}>
          {shouldShowEmptyState ? (
            <View style={styles.emptyOverlay} pointerEvents="auto">
              <ChatEmptyState
                title={workspaceName}
                input={
                  <ChatInputController
                    onSubmit={handleSubmit}
                    disabled={isStreaming}
                    placeholder="Type a message..."
                    activeWorkspaceType={currentWorkspaceType}
                    showSuggestions={true}
                    preselectedAssignment={preselectedAssignment}
                    showError={showError}
                  />
                }
                onAssignmentPress={handleAssignmentPress}
              />
            </View>
          ) : (
            <View style={styles.messagesArea}>
              <MessagesContainer
                messages={messages}
                isStreaming={isStreamingForCurrentChat}
                currentStreamingMessageId={currentStreamingMessageId}
                resetAutoScroll={resetAutoScroll}
                onStartEdit={chatId ? handleStartEdit : undefined}
                editingMessageId={editingMessage?.id ?? null}
                editingMessageDraft={editingMessage?.draft ?? null}
                onChangeEditingMessageDraft={handleChangeEditingDraft}
                onSaveEdit={handleSaveInlineEdit}
                onCancelEdit={handleCancelEdit}
                onSwitchBranch={chatId ? handleSwitchBranch : undefined}
                feedbackByMessageId={feedbackByMessageId}
                onFeedback={handleFeedback}
                onOpenLearningActivity={handleOpenLearningActivity}
                streamStatusMessage={streamStatusMessage}
                showIleCreationLoader={showIleCreationLoader}
                ileCreationErrorMessage={scopedIleCreationErrorMessage}
                deepResearchState={scopedDeepResearchState}
                onCancelDeepResearch={
                  scopedDeepResearchState?.phase === "progress" && scopedDeepResearchState.progress
                    ? handleCancelDeepResearch
                    : undefined
                }
                getAssistantReadAloudControl={getAssistantReadAloudControl}
              />
              <View style={styles.chatInputWrapper}>
                <ChatInputController
                  onSubmit={handleSubmit}
                  disabled={isStreaming}
                  placeholder="Type a message..."
                  activeWorkspaceType={currentWorkspaceType}
                  isEmpty={messages.length === 0}
                  showError={showError}
                />
                <View style={styles.disclaimerContainer}>
                  <CustomText
                    useTranslationText={false}
                    text="Axio is AI and can make mistakes. Please double-check responses."
                    style={styles.disclaimerText}
                  />
                </View>
              </View>
            </View>
          )}
        </View>
        <CompanionErrorModal
          visible={errorModalVisible}
          title={errorTitle}
          message={errorMessage}
          onClose={() => setErrorModalVisible(false)}
        />
        <ThreadModals
          renameModalOpen={renameModalOpen}
          setRenameModalOpen={setRenameModalOpen}
          renameValue={renameValue}
          setRenameValue={setRenameValue}
          onRenameSubmit={handleRenameSubmit}
          deleteModalOpen={false}
          setDeleteModalOpen={() => {}}
          selectedThreadTitle=""
          onDeleteConfirm={() => {}}
        />
      </View>
    </MathVirtualKeyboardProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: semanticColors.surface.white,
    display: "flex",
    flexDirection: "column",
  },
  chatInputWrapper: {
    width: "100%",
    maxWidth: 1152,
    alignSelf: "center",
    zIndex: 900,
    padding: spacing.sm,
  },
  content: {
    flex: 1,
    position: "relative",
  },
  emptyOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    elevation: 9999,
    backgroundColor: semanticColors.surface.light,
  },
  messagesArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: semanticColors.surface.light,
  },
  threadSelector: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: [{ translateX: "-50%" }, { translateY: "-50%" }],
  },
  disclaimerContainer: {
    alignItems: "center",
  },
  disclaimerText: {
    ...typographyStyles.textXs,
    color: semanticColors.typography.textLight,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: spacing.lg,
  },
  errorMessage: {
    textAlign: "center",
    marginBottom: spacing.sm,
  },
  retryButton: {
    minWidth: 120,
  },
  threadTitleButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: radii.full,
    maxWidth: "100%",
  },
  threadTitleButtonHovered: {
    backgroundColor: colors.grey[100],
  },
  threadTitleButtonText: {
    color: colors.grey[700],
  },
  threadTitleIconWrap: {
    width: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  threadTitleIconHidden: {
    opacity: 0,
  },
});

export default ChatWindowContainer;
